import { ANALYTICS_EVENTS } from '@syncanix/shared';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { track } from '@/analytics/track';

import { ErrorFallback } from './ErrorFallback';

/**
 * Marketing-site React error boundary (Phase 7 v1.1 iter 304).
 *
 * Catches uncaught exceptions thrown by descendant components and
 * renders a fallback UI in their place. Without this, any throw in
 * the page tree would unmount the whole subtree and the user would
 * see a blank page — the worst-case UX for a bug we can't catch in
 * code review.
 *
 * **Mounted around `<Routes />` in `App.tsx`** (not at `<App />`
 * boundary): the SkipLink + Footer stay rendered when an error
 * occurs, so the user still has site chrome + accessibility
 * affordances. Only the routed page region replaces with the
 * fallback.
 *
 * **Class component** (Lead Dev hat, locked): React's error-boundary
 * contract requires `getDerivedStateFromError` + `componentDidCatch`,
 * both of which are class-component-only. There's no hook equivalent
 * as of React 18.
 *
 * **No logger wiring in v1** (Architect hat, locked): a v1.1 polish
 * iter wires Sentry / Better Stack into `componentDidCatch` for
 * production crash reporting. v1 ships with `console.error` (which
 * React calls automatically before the boundary catches) — operator
 * sees the error in browser dev-tools during pre-launch QA but no
 * remote crash-reporting yet (BL-016 + BL-017 in BLOCKERS.md when
 * those land).
 *
 * **No "try again" button** (Designer hat, locked): the fallback
 * offers navigation deep-links instead of a re-render-the-same-tree
 * retry. Reasoning: most marketing-page crashes are bugs in our
 * code, not transient failures — retrying the same tree usually
 * re-throws. The two deep-links (home + trust center) are the most
 * useful next-actions a customer can take on a v1 marketing site.
 */

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(_error: unknown): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // B20 - production crashes were operator-INVISIBLE (this was a no-op;
    // console.error only reaches whoever has dev-tools open). Report through
    // the existing PostHog rail - message + a trimmed component stack +
    // the path, never user content. `track` is a no-op without a key
    // (local dev), and a throw inside a crash handler must never cascade.
    try {
      const message = error instanceof Error ? error.message : String(error);
      track(ANALYTICS_EVENTS.MARKETING_ERROR_BOUNDARY_CAUGHT, {
        message: message.slice(0, 300),
        componentStack: (info.componentStack ?? '').slice(0, 600),
        path: typeof location !== 'undefined' ? location.pathname : '',
      });
    } catch {
      // Reporting is best-effort by definition.
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}
